/**
 * SkillsPage
 * Main page for browsing and managing skills
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { AppLayout } from '../../components/layout';
import { SkillCard, SkillFilters, SkillBrowseCategory } from '../../components/skills';
import { Button, Pagination, ConfirmModal } from '../../components/ui';
import { skillsApi } from '../../services/api';
import type { Skill } from '../../types';
import styles from './SkillsPage.module.css';

// Available skill categories
const SKILL_CATEGORIES = [
  'Business Analysis',
  'Code Generation',
  'Code Review',
  'Data Analysis',
  'DevOps',
  'Documentation',
  'Project Management',
  'Quality Assurance',
  'Security',
  'Testing',
  'Architecture',
  'Design',
  'Research',
  'Custom',
];

export const SkillsPage: React.FC = () => {
  const navigate = useNavigate();

  // Skills data
  const [skills, setSkills] = useState<Skill[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [selectedBrowseCategory, setSelectedBrowseCategory] = useState<SkillBrowseCategory>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [createdByMe, setCreatedByMe] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState<string>('');
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'private' | 'project'>('all');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  // Delete confirmation modal
  const [skillToDelete, setSkillToDelete] = useState<Skill | null>(null);

  // Load skills from API
  const loadSkills = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await skillsApi.list();
      if (response.success && response.data) {
        // Backend returns data as array directly, or as { skills: [] }
        const skillsList = Array.isArray(response.data)
          ? response.data
          : (response.data as any).skills || [];
        setSkills(skillsList);
      }
    } catch (err) {
      console.error('Failed to load skills:', err);
      setError('Failed to load skills. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSkills();
  }, [loadSkills]);

  // Filter skills based on current filters
  const getFilteredSkills = (): Skill[] => {
    let filtered = [...skills];

    // Filter by browse category (project vs marketplace vs all)
    if (selectedBrowseCategory === 'marketplace') {
      filtered = filtered.filter((s) => s.visibility === 'public');
    } else if (selectedBrowseCategory === 'project') {
      filtered = filtered.filter((s) => s.visibility === 'private' || s.visibility === 'project');
    }
    // 'all' or any other value shows everything

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.name.toLowerCase().includes(query) ||
          s.displayName.toLowerCase().includes(query) ||
          s.description.toLowerCase().includes(query)
      );
    }

    // Filter by categories
    if (selectedCategories.length > 0) {
      filtered = filtered.filter((s) =>
        s.categories.some((cat) => selectedCategories.includes(cat))
      );
    }

    // Filter by created by me
    if (createdByMe) {
      // TODO: Replace with actual user ID check
      filtered = filtered.filter((s) => s.authorId === 'current-user-id');
    }

    // Filter by visibility
    if (visibilityFilter !== 'all') {
      filtered = filtered.filter((s) => s.visibility === visibilityFilter);
    }

    return filtered;
  };

  const filteredSkills = getFilteredSkills();
  const totalPages = Math.ceil(filteredSkills.length / itemsPerPage);
  const paginatedSkills = filteredSkills.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handlers
  const handleCreateSkill = () => {
    navigate('/skills/create');
  };

  const handleSkillClick = (skill: Skill) => {
    navigate(`/skills/${skill.id}/edit`);
  };

  const handleEditSkill = (skill: Skill) => {
    navigate(`/skills/${skill.id}/edit`);
  };

  const handleDeleteSkill = (skill: Skill) => {
    setSkillToDelete(skill);
  };

  const confirmDelete = async () => {
    if (!skillToDelete) return;

    try {
      await skillsApi.delete(skillToDelete.id);
      await loadSkills();
      setSkillToDelete(null);
    } catch (err) {
      console.error('Failed to delete skill:', err);
      setError('Failed to delete skill. Please try again.');
    }
  };

  const handleDuplicateSkill = async (skill: Skill) => {
    try {
      await skillsApi.duplicate(skill.id);
      await loadSkills();
    } catch (err) {
      console.error('Failed to duplicate skill:', err);
      setError('Failed to duplicate skill. Please try again.');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const sidebar = (
    <SkillFilters
      selectedBrowseCategory={selectedBrowseCategory}
      onBrowseCategoryChange={setSelectedBrowseCategory}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      selectedProject={selectedProject}
      onProjectChange={setSelectedProject}
      availableCategories={SKILL_CATEGORIES}
      selectedCategories={selectedCategories}
      onCategoriesChange={setSelectedCategories}
      createdByMe={createdByMe}
      onCreatedByMeChange={setCreatedByMe}
      selectedCreator={selectedCreator}
      onCreatorChange={setSelectedCreator}
      visibilityFilter={visibilityFilter}
      onVisibilityFilterChange={setVisibilityFilter}
    />
  );

  return (
    <AppLayout sidebar={sidebar}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.count}>
            {filteredSkills.length} {filteredSkills.length === 1 ? 'SKILL' : 'SKILLS'}
          </h2>
          <Button
            variant="primary"
            size="md"
            leftIcon={<Plus size={16} />}
            onClick={handleCreateSkill}
          >
            Create Skill
          </Button>
        </div>

        {error && (
          <div className={styles.error}>
            {error}
          </div>
        )}

        {isLoading ? (
          <div className={styles.loading}>Loading skills...</div>
        ) : filteredSkills.length === 0 ? (
          <div className={styles.empty}>No skills found.</div>
        ) : (
          <>
            <div className={styles.grid}>
              {paginatedSkills.map((skill) => (
                <SkillCard
                  key={skill.id}
                  skill={skill}
                  onClick={handleSkillClick}
                  onEdit={handleEditSkill}
                  onDelete={handleDeleteSkill}
                  onDuplicate={handleDuplicateSkill}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {skillToDelete && (
        <ConfirmModal
          isOpen={true}
          title="Delete Skill"
          message={`Are you sure you want to delete "${skillToDelete.displayName}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDelete}
          onClose={() => setSkillToDelete(null)}
          variant="danger"
        />
      )}
    </AppLayout>
  );
};

export default SkillsPage;
